import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, makeStyles, Tooltip } from '@material-ui/core';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { AtNoData } from '../../../components/AtNoData';
import StarIcon from '@material-ui/icons/Star';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import RemoveIcon from '@material-ui/icons/Remove';
import { useSentimientoReviews } from '../../../hooks/useSentimientoReviews';
import { useTranslation } from 'react-i18next';

const getColumns = (t) => [
    { key: "fecha", label: t('sentiment.reviewsTable.cols.date'), align: "left" },
    { key: "app", label: t('sentiment.reviewsTable.cols.app'), align: "left" },
    { key: "autor", label: t('sentiment.reviewsTable.cols.author'), align: "left" },
    { key: "rating", label: t('sentiment.reviewsTable.cols.rating'), align: "center" },
    { key: "comentario", label: t('sentiment.reviewsTable.cols.comment'), align: "left" },
    { key: "sentimiento", label: t('sentiment.reviewsTable.cols.sentiment'), align: "center" },
    { key: "insight", label: t('sentiment.reviewsTable.cols.insight'), align: "left" },
];


const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    title: {
        fontSize: '16px',
        fontWeight: 700,
        color: '#374151',
    },
    tableContainer: {
        borderRadius: '6px',
        border: '1px solid #E5E7EB',
        '&::-webkit-scrollbar': { height: '6px', width: '6px' },
        '&::-webkit-scrollbar-track': { background: '#f9fafb' },
        '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: '4px' },
        '& .MuiCircularProgress-colorPrimary': {
            color: '#6366F1',
        },
    },
    headCell: {
        fontWeight: 600,
        color: '#6B7280',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        borderBottom: '2px solid #E5E7EB',
        padding: '10px 14px',
        backgroundColor: '#F9FAFB',
        whiteSpace: 'nowrap',
    },
    bodyRow: {
        '&:nth-of-type(even)': { backgroundColor: '#FAFBFC' },
        '&:hover': { backgroundColor: '#F3F4F6' },
        transition: 'background-color 0.15s ease',
    },
    cell: {
        fontSize: '12px',
        fontWeight: 400,
        color: '#374151',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 14px',
        lineHeight: '1.5',
    },
    cellFecha: {
        whiteSpace: 'nowrap',
        fontWeight: 500,
        color: '#6366F1',
    },
    cellApp: {
        fontWeight: 600,
        color: '#374151',
        '& span': {
            display: 'block',
            fontSize: '10px',
            fontWeight: 400,
            color: '#9CA3AF',
            marginTop: '1px',
        },
    },
    cellComentario: {
        maxWidth: '280px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    cellInsight: {
        maxWidth: '200px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontStyle: 'italic',
        color: '#6B7280',
    },
    starContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
    },
    starFilled: {
        fontSize: '14px',
        color: '#F59E0B',
    },
    starEmpty: {
        fontSize: '14px',
        color: '#E5E7EB',
    },
    iconPositivo: {
        backgroundColor: '#D1FAE5',
        color: '#059669',
        padding: '6px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconNegativo: {
        backgroundColor: '#FEE2E2',
        color: '#DC2626',
        padding: '6px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconNeutral: {
        backgroundColor: '#FEF3C7',
        color: '#D97706',
        padding: '6px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chipPendiente: {
        color: '#9CA3AF',
        fontSize: '11px',
        fontStyle: 'italic',
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        padding: theme.spacing(0, 1),
    },
    pageInfo: {
        backgroundColor: '#6366F1',
        color: '#FFFFFF',
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 14px',
        borderRadius: '6px',
        display: 'inline-block',
    },
    paginationBtn: {
        color: '#374151',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '12px',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        padding: '4px 16px',
        '&:hover': { backgroundColor: '#F3F4F6' },
        '&:disabled': { color: '#D1D5DB', borderColor: '#F3F4F6' },
    },
}));

const renderHeader = (classes, t) => (
    <TableHead>
        <TableRow>
            {getColumns(t).map((col) => (
                <TableCell key={col.key} align={col.align} className={classes.headCell}>
                    {col.label}
                </TableCell>
            ))}
        </TableRow>
    </TableHead>
);

const renderStars = (rating, classes) => (
    <Box className={classes.starContainer}>
        {[1, 2, 3, 4, 5].map(i => (
            <StarIcon
                key={i}
                className={i <= rating ? classes.starFilled : classes.starEmpty}
            />
        ))}
    </Box>
);

const renderSentiment = (sentiment, classes, t) => {
    if (!sentiment || sentiment === t('sentiment.reviewsTable.pending')) {
        return <span className={classes.chipPendiente}>{t('sentiment.reviewsTable.pending')}</span>;
    }
    const upper = sentiment.toUpperCase();
    
    if (upper === 'POSITIVO') {
        return (
            <Tooltip title="Positivo">
                <Box className={classes.iconPositivo}>
                    <ThumbUpIcon style={{ fontSize: '16px' }} />
                </Box>
            </Tooltip>
        );
    } else if (upper === 'NEGATIVO') {
        return (
            <Tooltip title="Negativo">
                <Box className={classes.iconNegativo}>
                    <ThumbDownIcon style={{ fontSize: '16px' }} />
                </Box>
            </Tooltip>
        );
    } else {
        return (
            <Tooltip title="Neutral">
                <Box className={classes.iconNeutral}>
                    <RemoveIcon style={{ fontSize: '16px' }} />
                </Box>
            </Tooltip>
        );
    }
};

const renderRow = (row, classes, t) => (
    <TableRow key={row.id} className={classes.bodyRow}>
        <TableCell className={`${classes.cell} ${classes.cellFecha}`}>
            {row.fecha}
        </TableCell>
        <TableCell className={`${classes.cell} ${classes.cellApp}`}>
            {row.app}
            <span>{row.tienda}</span>
        </TableCell>
        <TableCell className={classes.cell}>{row.autor}</TableCell>
        <TableCell className={classes.cell} align="center">
            {renderStars(row.rating, classes)}
        </TableCell>
        <TableCell className={`${classes.cell} ${classes.cellComentario}`}>
            {row.comentario}
        </TableCell>
        <TableCell className={classes.cell} align="center">
            {renderSentiment(row.sentimiento, classes, t)}
        </TableCell>
        <TableCell className={`${classes.cell} ${classes.cellInsight}`}>
            {row.insight}
        </TableCell>
    </TableRow>
);

const renderEmpty = (isLoading, t) => (
    <TableRow>
        <TableCell colSpan={getColumns(t).length} align="center" style={{ padding: 0 }}>
            {isLoading ? (
                <Box p={4}><AtCircularProgress /></Box>
            ) : (
                <AtNoData message={t('sentiment.reviewsTable.noReviews')} minHeight={200} />
            )}
        </TableCell>
    </TableRow>
);

const renderBody = (rows, classes, isLoading, t) => (
    <TableBody>
        {rows.length === 0 ? renderEmpty(isLoading, t) : rows.map(row => renderRow(row, classes, t))}
    </TableBody>
);

const renderPagination = (currentPage, totalPages, setCurrentPage, classes, t) => (
    <Box className={classes.paginationContainer}>
        <Button
            className={classes.paginationBtn}
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
        >
            {t('sentiment.reviewsTable.previous')}
        </Button>
        <span className={classes.pageInfo}>
            {t('sentiment.reviewsTable.page')} {currentPage} {t('sentiment.reviewsTable.of')} {totalPages}
        </span>
        <Button
            className={classes.paginationBtn}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
        >
            {t('sentiment.reviewsTable.next')}
        </Button>
    </Box>
);

const ReviewsTable = ({ filters }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch the data from the new endpoint
    const { data: response, isLoading } = useSentimientoReviews(filters, currentPage);
    const apiData = response?.data || response;
    
    const rows = apiData?.data || [];
    const totalPages = apiData?.totalPages || 1;

    // Reset pagination to page 1 whenever filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.title}>{t('sentiment.reviewsTable.title')}</Typography>
            </Box>

            <TableContainer className={classes.tableContainer}>
                <Table size="small">
                    {renderHeader(classes, t)}
                    {renderBody(rows, classes, isLoading, t)}
                </Table>
            </TableContainer>

            {renderPagination(currentPage, totalPages, setCurrentPage, classes, t)}
        </Box>
    );
};

export default ReviewsTable;
